// <CalendarWeekRow> — T-230 + T-231 (time-view-week-implementation-plan
// §6.1 + §6.5).
//
// Single employee row in the Week-variant calendar: a 180px frozen
// employee cell + 7 day cells. Each entry that intersects the row's
// week renders as a colored bar in the day cells it covers.
//
// T-231 adds the stacking rule per spec §6.5: when ≥3 entries overlap
// a single day-cell, render 2 bars + a "+N more" chip whose click
// opens the <CalendarWeekStackPopover>.

import { useState } from 'react';
import type { CalendarWeekEntry, CalendarWeekRow } from '@pa/shared/time-view';
import CalendarWeekStackPopover from './CalendarWeekStackPopover';

interface Props {
  row: CalendarWeekRow;
  /** ISO `YYYY-MM-DD` strings of the visible days, in column order. */
  weekDays: string[];
  /**
   * ISO `YYYY-MM-DD` of today (or null when today is outside the
   * visible week — the row then renders without any today tint).
   */
  todayIso: string | null;
  /** Day-cell width in pixels. Caller controls hide-weekends widening. */
  dayColWidth?: number;
}

const STICKY_COL_W = 180;
const DEFAULT_DAY_COL_W = 160;

// Stacking cap per plan §6.5 — render at most 2 bars before the
// "+N more" chip takes over.
const STACK_CAP = 2;

/**
 * Pure helper — finds every entry that covers `dayIso`, sorted by
 * startDate descending (most-recent first) per spec §6.5.
 */
function entriesOnDay(entries: CalendarWeekEntry[], dayIso: string): CalendarWeekEntry[] {
  return entries
    .filter((e) => e.startDate <= dayIso && e.endDate >= dayIso)
    .slice()
    .sort((a, b) => (a.startDate < b.startDate ? 1 : a.startDate > b.startDate ? -1 : 0));
}

/**
 * Pure helper — returns true when `dayIso` is the start day of `entry`
 * (or `entry.startDate` is earlier than the visible week, in which
 * case the entry's "start day in this week" is the week's first day).
 *
 * The label is rendered only on the bar's first cell so multi-day
 * spans look like a single continuous pill.
 */
function isLabelDay(entry: CalendarWeekEntry, dayIso: string, weekStart: string): boolean {
  if (entry.startDate >= weekStart) return entry.startDate === dayIso;
  return weekStart === dayIso;
}

export default function CalendarWeekRow({
  row,
  weekDays,
  todayIso,
  dayColWidth = DEFAULT_DAY_COL_W,
}: Props) {
  const weekStart = weekDays[0] ?? '';
  // Track which cell's "+N more" popover (if any) is open. `null`
  // when no popover is open. Single-popover-at-a-time scope per spec.
  const [openCellIso, setOpenCellIso] = useState<string | null>(null);

  return (
    <div
      data-testid={`week-row-${row.employeeId}`}
      className="flex items-stretch border-b border-slate-100 hover:bg-slate-50/60"
    >
      {/* Sticky employee cell. */}
      <div
        className="flex-none flex items-center gap-3 px-4 py-3 border-r border-slate-100 bg-white"
        style={{ width: STICKY_COL_W }}
      >
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white ${row.avatarColor}`}
          aria-hidden
        >
          {row.name.slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate">
            {row.name}
          </div>
          <div className="text-xs text-slate-500 truncate">{row.role}</div>
        </div>
      </div>

      {/* Visible day cells. */}
      {weekDays.map((dayIso) => {
        const dayEntries = entriesOnDay(row.entries, dayIso);
        const isToday = dayIso === todayIso;
        const overflow = dayEntries.length - STACK_CAP;
        const visibleEntries =
          overflow > 0 ? dayEntries.slice(0, STACK_CAP) : dayEntries;
        return (
          <div
            key={dayIso}
            data-testid={`week-cell-${row.employeeId}-${dayIso}`}
            className={`relative flex-none border-r border-slate-100 px-1.5 py-2 flex flex-col gap-1 min-h-[88px] ${
              isToday ? 'bg-[#A6264C]/5' : ''
            }`}
            style={{ width: dayColWidth }}
          >
            {visibleEntries.map((entry) => (
              <div
                key={entry.id}
                data-testid="week-cell-bar"
                data-entry-id={entry.id}
                className={`${entry.policyColor} rounded px-1.5 py-0.5 text-[10px] font-semibold text-white truncate ${
                  entry.status === 'pending' ? 'opacity-60' : ''
                }`}
                title={`${entry.policyName} · ${entry.startDate} – ${entry.endDate}`}
              >
                {isLabelDay(entry, dayIso, weekStart) ? entry.policyName : ' '}
              </div>
            ))}
            {overflow > 0 && (
              <button
                type="button"
                data-testid={`week-cell-more-${row.employeeId}-${dayIso}`}
                onClick={() => setOpenCellIso(dayIso)}
                className="text-[9px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 hover:bg-slate-200 self-start"
              >
                +{overflow} more
              </button>
            )}
            {openCellIso === dayIso && (
              <CalendarWeekStackPopover
                dayIso={dayIso}
                entries={dayEntries}
                onClose={() => setOpenCellIso(null)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
